import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Youtube, 
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Smartphone,
  Shield,
  Users,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setIsSubscribing(true);

    try {
      const response = await fetch(`${window.location.origin}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Successfully subscribed! 🎉');
        setEmail('');
      } else {
        toast.error(data.message || 'Subscription failed');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'What is Fokatmain', href: '/what-is-fokatmain' },
    { name: 'Play Games', href: '/play-games' },
    { name: 'Winners Gallery', href: '/winners' },
    { name: 'How it Works', href: '/#how-it-works' },
    { name: 'About Us', href: '/about' },
    { name: 'For Brands', href: '/brands' },
    { name: 'Contact Us', href: '/contact' }
  ];

  const legalLinks = [
    { name: 'FAQ', href: '/faq' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Gaming Policy', href: '/gaming-policy' },
    { name: 'Refund Policy', href: '/refund-policy' },
    { name: 'Responsible Gaming', href: '/terms#responsible' }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/fokatmain' },
    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/fokatmain' },
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/fokatmain' },
    { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@fokatmain' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/fokatmain' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border-t border-gray-800">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-4">
              Stay Updated with Latest Games & Prizes
            </h3>
            <p className="text-gray-300 mb-8">
              Get notified about new games, exclusive tournaments, and amazing prize drops.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubscribe()}
                disabled={isSubscribing}
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-yellow-400"
              />
              <Button 
                onClick={handleSubscribe}
                disabled={isSubscribing}
                className="btn-primary px-6 py-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <img 
                src="/fokatmain_round_logo.jpeg" 
                alt="Fokatmain Logo" 
                className="h-16 w-16 logo-round border-2 border-yellow-400/30 hover:border-yellow-400 hover:scale-105 transition-all duration-300"
              />
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              India's premier skill-based gaming platform. Play quick games, win real gadgets. 
              No cash prizes, just amazing tech rewards.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center space-x-2 text-green-400">
                <Shield className="w-4 h-4" />
                <span className="text-xs">100% Secure & Fair</span>
              </div>
              <div className="flex items-center space-x-2 text-yellow-400">
                <Trophy className="w-4 h-4" />
                <span className="text-xs">UPI Payments</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-400">
                <Users className="w-4 h-4" />
                <span className="text-xs">Growing Community</span>
              </div>
            </div>

          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-6">
              Legal & Support
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-6">
              Get in Touch
            </h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-400 text-sm">Email Support</p>
                  <a href="mailto:support@fokatmain.com" className="text-white text-sm hover:text-yellow-400 transition-colors">
                    support@fokatmain.com
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-6">
              <h5 className="font-semibold text-white mb-3">Follow Us</h5>
              <div className="flex space-x-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      className="p-2 bg-gray-800 hover:bg-yellow-400 text-gray-400 hover:text-black rounded-full transition-all duration-300 hover:scale-110"
                      aria-label={social.name}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 bg-black/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-gray-400">
              <p>© {currentYear} PLAYEDGE INNOVATIONS. All rights reserved.</p>
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span>Play Responsibly</span>
                </span>
                <span>18+ Only</span>
                <span>Skill-Based Gaming</span>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 text-center md:text-right">
              <p>Fokatmain is a skill-based gaming platform.</p>
              <p>Not gambling. Gadget prizes only, no cash payouts.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;